import { useCallback, useRef, useState } from 'react';

/** Configuration for {@link useControllableState}. */
interface UseControllableStateParams<T> {
  /** The controlled value. If defined, the component is controlled. */
  prop?: T;
  /** The initial value when uncontrolled. */
  defaultProp?: T;
  /** Callback fired whenever the value changes (controlled or uncontrolled). */
  onChange?: (value: T) => void;
}

/**
 * A hook that supports both controlled and uncontrolled component patterns.
 *
 * When `prop` is provided, the component is **controlled** — the hook returns
 * `prop` as the current value and delegates updates to the `onChange` callback.
 * When `prop` is `undefined`, the component is **uncontrolled** — the hook
 * manages its own internal state initialized with `defaultProp`.
 *
 * @template T - The type of the state value.
 * @param params - See {@link UseControllableStateParams}.
 * @returns A tuple of `[value, setValue]`, mirroring the `useState` API.
 *
 * @example
 * ```tsx
 * // Uncontrolled usage
 * const [value, setValue] = useControllableState({ defaultProp: '' });
 *
 * // Controlled usage
 * const [value, setValue] = useControllableState({
 *   prop: controlledValue,
 *   onChange: setControlledValue,
 * });
 * ```
 */
function useControllableState<T>({
  prop,
  defaultProp,
  onChange,
}: UseControllableStateParams<T>): [T, (value: T | ((prev: T) => T)) => void] {
  const [uncontrolledValue, setUncontrolledValue] = useState<T>(defaultProp as T);
  const isControlled = prop !== undefined;
  const value = isControlled ? prop : uncontrolledValue;

  const onChangeRef = useRef(onChange);
  // eslint-disable-next-line react-hooks/refs
  onChangeRef.current = onChange;

  const setValue = useCallback(
    (nextValue: T | ((prev: T) => T)) => {
      if (isControlled) {
        const resolved =
          typeof nextValue === 'function' ? (nextValue as (prev: T) => T)(prop as T) : nextValue;
        onChangeRef.current?.(resolved);
      } else {
        setUncontrolledValue((prev) => {
          const resolved = typeof nextValue === 'function' ? (nextValue as (prev: T) => T)(prev) : nextValue;
          onChangeRef.current?.(resolved);
          return resolved;
        });
      }
    },
    [isControlled, prop]
  );

  return [value, setValue] as const;
}

export { useControllableState };
export type { UseControllableStateParams };
